import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Cookiebot consent categories.
 * - necessary:   Always allowed. No consent required.
 * - preferences: Functional/personalisation cookies (e.g. embedded forms).
 * - statistics:  Analytics/performance cookies (e.g. YouTube analytics).
 * - marketing:   Advertising/tracking cookies (e.g. Google Ads, video embeds).
 *
 * Craft field values map directly to these:
 *   required    → necessary
 *   preferences → preferences
 *   statistics  → statistics
 *   marketing   → marketing
 */
export type CookieConsentCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing'

/**
 * Minimal Cookiebot SDK surface used by this composable.
 * Declared here to avoid relying on a third-party type package.
 */
interface CookiebotConsent {
    necessary: boolean
    preferences: boolean
    statistics: boolean
    marketing: boolean
}

interface CookiebotSDK {
    consent: CookiebotConsent
    renew: () => void
}

declare global {
    interface Window {
        Cookiebot?: CookiebotSDK
    }
}

/**
 * Opens the Cookiebot consent dialog so the user can manage preferences.
 * Safe to call even if Cookiebot has not yet loaded.
 */
function openConsentDialog() {
    (globalThis as unknown as { Cookiebot?: CookiebotSDK }).Cookiebot?.renew()
}

/**
 * useCookieConsent
 *
 * Reactive composable that tracks whether the user has accepted ALL of the
 * specified Cookiebot consent categories. Components consuming this composable
 * will reactively update when:
 *   - Cookiebot determines consent state on page load  (CookiebotOnConsentReady)
 *   - The user accepts the required categories         (CookiebotOnAccept)
 *   - The user withdraws or declines consent           (CookiebotOnDecline)
 *
 * If 'necessary' is included in the categories array, consent is always
 * considered granted and no gate is shown.
 *
 * If ALL specified categories have been accepted, hasConsent becomes true.
 * If consent is later withdrawn, hasConsent immediately reverts to false and
 * the consent gate placeholder is shown again.
 *
 * @param categories - One or more Cookiebot consent categories that must all
 *                     be accepted before content is shown.
 *
 * Usage:
 *   const { hasConsent, openConsentDialog } = useCookieConsent(['preferences'])
 *   const { hasConsent, openConsentDialog } = useCookieConsent(['preferences', 'statistics'])
 */
export function useCookieConsent(categories: CookieConsentCategory[]) {
    const hasConsent = ref(false)

    function checkConsent() {
        // If 'necessary' is one of the required categories, no gate is needed
        if (categories.includes('necessary')) {
            hasConsent.value = true
            return
        }

        const cookiebot = (globalThis as unknown as { Cookiebot?: CookiebotSDK }).Cookiebot

        // All specified categories must be accepted
        hasConsent.value = categories.every(
            category => !!(cookiebot?.consent?.[category]),
        )
    }

    function onCookiebotAccept() {
        checkConsent()
    }

    function onCookiebotDecline() {
        checkConsent()
    }

    function onCookiebotConsentReady() {
        checkConsent()
    }

    onMounted(() => {
        // Check immediately in case Cookiebot already fired before this component mounted
        checkConsent()

        // Consent granted on this page visit
        globalThis.addEventListener('CookiebotOnAccept', onCookiebotAccept)
        // Consent withdrawn or declined — hide content immediately
        globalThis.addEventListener('CookiebotOnDecline', onCookiebotDecline)
        // Fires on every page load once Cookiebot has determined the consent state;
        // catches revoked consent from a previous session
        globalThis.addEventListener('CookiebotOnConsentReady', onCookiebotConsentReady)
    })

    onUnmounted(() => {
        globalThis.removeEventListener('CookiebotOnAccept', onCookiebotAccept)
        globalThis.removeEventListener('CookiebotOnDecline', onCookiebotDecline)
        globalThis.removeEventListener('CookiebotOnConsentReady', onCookiebotConsentReady)
    })

    return {
        hasConsent,
        openConsentDialog,
    }
}
